import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Spin, Tag, Tooltip } from 'antd';
import { DropdownMenu } from '@/components';
import PlanningCard from '@/components/PlanningCard';
import { ExclamationCircleOutlined } from '@ant-design/icons';

import { STUDY_PLAN_DELETE_BY_ADMIN } from '../../../constants/permissions';
import { IN_PROGRESS } from '../../../constants/study-plan-statuses';
import { StatusPopover } from '../status-popover';

import useIsMobile from '@shared/hooks/useIsMobile';
import { classNames } from '@shared/utils/styles-utils';
import { isAnyCorporateOrExternalCourse, isExecutiveTask } from '@shared/utils/topic-utils';

import _get from 'lodash/get';
import PropTypes from 'prop-types';

import styles from './styles.module.scss';

const cn = classNames(styles);

export const Card = ({ material, currentUser, blockName, actions, onAction, loading }) => {
  const { t } = useTranslation('planningPage');

  const { topic, status, showStudyButton, addedByAdmin, trackInfo } = material;
  const topicTypeId = _get(topic, 'type.id');
  const withEvents = _get(topic, 'type.withEvents');

  const [isMobile] = useIsMobile(800);

  const navigate = useNavigate();

  const _onAction = action => () => onAction({ action, material, blockName });

  const _actions =
    !!actions &&
    material?.isCurrent &&
    actions({
      status,
      typeId: topicTypeId,
      showStudyButton,
      addedByAdmin,
      track: trackInfo,
    });

  const mainActions = _actions && _actions.main;

  const REMOVE_FROM_PLAN = isMobile ? t('removeFromPlanMobile').toUpperCase() : t('removeFromPlan').toUpperCase();
  const RESCHEDULE = t('reschedule').toUpperCase();

  const inProgress = material.status === IN_PROGRESS;
  const showRemoveFromPlan = !addedByAdmin && !withEvents;

  const additionalActions = Array.isArray(_actions?.additional)
    ? _get(_actions, 'additional', [])
        .map(item => ({ key: item.name, label: item.label }))
        .filter(item => {
          const removeButton = item.label.toUpperCase().includes(REMOVE_FROM_PLAN);

          const hideButton =
            (addedByAdmin && !currentUser.authorities.includes(STUDY_PLAN_DELETE_BY_ADMIN) && removeButton) ||
            (addedByAdmin && item.label.toUpperCase().includes(RESCHEDULE));

          if (removeButton && inProgress) {
            return showRemoveFromPlan;
          }

          return !hideButton;
        })
    : [];

  const onClickAdditionalMenu = params => _onAction(params.key)();

  const getIcon = Icon => (Icon ? <Icon /> : null);

  const handleDateRedirect = () => {
    if (material?.topic?.id) navigate(`/collection-topics/${material?.topic?.id}?tab=events`);
  };

  const actionsRender = useMemo(() => {
    if (!mainActions) return <></>;

    if (!isExecutiveTask(topic?.type?.id)) {
      return (
        <div className={cn('planning-card__actions')}>
          {mainActions.map(action => (
            <Button
              key={action.name}
              type='link'
              onClick={_onAction(action.name)}
              className={cn('planning-card__action')}
            >
              {getIcon(action.icon)} {action.label}
            </Button>
          ))}
        </div>
      );
    } else {
      return (
        <div className={cn('planning-card__actions')}>
          {mainActions.map(action => (
            <Button
              key={action.name}
              type='link'
              onClick={_onAction('goToStudy')}
              className={cn('planning-card__action')}
            >
              {getIcon(action.icon)} {isMobile ? t('goToProcessMobile') : t('goToProcess')}
            </Button>
          ))}
        </div>
      );
    }
  }, [topic?.type?.id, mainActions]);

  const isDateRequirement = useMemo(() => {
    if (isAnyCorporateOrExternalCourse(topic?.type?.id)) {
      if (material?.addedByAdmin && !material?.event) return true;
    } else {
      return false;
    }
  }, [material, topic?.type?.id]);

  return (
    <>
      <PlanningCard key={material?.id} material={material} loading={loading} isPlanning>
        {material.loading ? (
          <Spin wrapperClassName={cn('planning-card__spinner')} />
        ) : (
          <>
            <div className={cn('planning-card__status')}>
              {material.info.nameUser}&nbsp;
              {!!material.info.description && <StatusPopover status={material.info} />}
            </div>

            {/* Действия */}
            {actionsRender}

            {/* Придупреждение, что нет выбранной даты */}
            {isDateRequirement && (
              <div className={cn('planning-card__actions')}>
                <Tooltip title={t('dateRequirementTooltip')} placement='bottomLeft'>
                  <Tag
                    className={cn('planning-card__dateRequirement')}
                    onClick={handleDateRedirect}
                    icon={<ExclamationCircleOutlined />}
                    color='red'
                  >
                    {t('dateRequirement')}
                  </Tag>
                </Tooltip>
              </div>
            )}

            {additionalActions.length ? (
              <DropdownMenu
                menu={additionalActions}
                onClick={onClickAdditionalMenu}
                className={cn('planning-card__button-more')}
              />
            ) : null}
          </>
        )}
      </PlanningCard>
      {/* <MaterialCard
        material={material}
        dataqa='userMaterialPlanningCard'
        className={cn('planning-card')}
        asideClass={cn('planning-card__aside')}
        currentUser={currentUser}
      >
        {material.loading ? (
          <Spin wrapperClassName={cn('planning-card__spinner')} />
        ) : (
          <>
            <div className={cn('planning-card__status')}>
              {material.info.nameUser}&nbsp;
              {!!material.info.description && <StatusPopover status={material.info} />}
            </div>
            {mainActions && !isExecutiveTask(topic.type.id) && (
              <div className={cn('planning-card__actions')}>
                {mainActions.map(action => (
                  <Button
                    key={action.name}
                    type='link'
                    onClick={_onAction(action.name)}
                    className={cn('planning-card__action')}
                  >
                    {getIcon(action.icon)} {action.label}
                  </Button>
                ))}
              </div>
            )}
            {mainActions && isExecutiveTask(topic.type.id) && (
              <div className={cn('planning-card__actions')}>
                {mainActions.map(action => (
                  <Button
                    key={action.name}
                    type='link'
                    onClick={_onAction('goToStudy')}
                    className={cn('planning-card__action')}
                  >
                    {getIcon(action.icon)} {isMobile ? t('goToProcessMobile') : t('goToProcess')}
                  </Button>
                ))}
              </div>
            )}
            {additionalActions.length ? (
              <DropdownMenu
                menu={additionalActions}
                onClick={onClickAdditionalMenu}
                className={cn('planning-card__button-more')}
              />
            ) : null}
          </>
        )}
      </MaterialCard> */}
    </>
  );
};

Card.propTypes = {
  actions: PropTypes.any,
  onAction: PropTypes.any,
  material: PropTypes.any,
  loading: PropTypes.bool,
  blocksFinished: PropTypes.bool,
  blockName: PropTypes.string,
  currentUser: PropTypes.object,
};
